import React, { useState, useEffect } from "react"
import { Add } from "@mui/icons-material"
import {
    Box,
    Fab,
    IconButton
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import dayjs from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import DataGrid from "../../components/DataGrid/DataGrid"
import { deleteReusableStep, showReusableProductionStepCreation, showReusableProductionStepView } from "../../actions/Steps/reusableProductionSteps."
import { getReusableProductionStepsPipelineSelector, getReusableStepsSupplierItemsSelector } from "../../reducers/Steps/steps"
import { getSupplierItemByName } from "../../parseManager/suppliers/supplierItems/parseSupplierItemManager"
import { REUSABLE_STEP_DATAGRID_STORAGE } from "../../utils/constant"
import CantDeleteModal from "../../components/CantDeleteModal"
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal"
import ReusableStepRebuildReportModal from "../Settings/TechnicalSheet/Resources/reusableSteps/ReusableStepRebuildReportModal"
import { isReusableStepInRecipes, isReusableStepUsedAsBase } from "../../parseManager/recipe/parseReusableProductionStepsManager"

const filterSupplierItems = (ingredients = []) => {
    const filteredSupplierItems = ingredients.filter((ingredient) => ingredient.supplierItem.isActive && !ingredient.supplierItem.deleted)

    return filteredSupplierItems
}

const ReusableSteps = () => {
    const [columns, setColumns] = useState([])
    const [data, setData] = useState([])
    const [hoveredRowId, setHoveredRowId] = useState(null)
    const [openCanNotDeleteDialog, setOpenCanNotDeleteDialog] = useState(false)
    const [rowToDelete, setRowToDelete] = useState(null)

    const dispatch = useDispatch()
    const steps = useSelector(getReusableProductionStepsPipelineSelector)
    const supplierItems = useSelector(getReusableStepsSupplierItemsSelector)

    const goToReusableStepCreation = () => dispatch(showReusableProductionStepCreation())

    const handleRowMouseEnter = (row) => {
        setHoveredRowId(row.deletion)
    }

    const handleRowMouseLeave = () => {
        setHoveredRowId(null)
    }

    const handleDelete = (id) => async (event) => {
        event.stopPropagation()
        const isUsedAsBase = await isReusableStepUsedAsBase(id)
        // open can not delete dialog if this step is used as base step
        if (isUsedAsBase) {
            setOpenCanNotDeleteDialog(true)
            return
        }

        const isUsedInRecipe = await isReusableStepInRecipes(id)

        // open can not delete dialog if this step is used in recipe
        if (isUsedInRecipe) {
            setOpenCanNotDeleteDialog(true)
            return
        }

        // open delete confirmation dialog
        const currentTransformation = steps.find((step) => step.objectId === id)
        setRowToDelete(currentTransformation)
    }

    useEffect(() => {
        setColumns([
            {
                key: "id",
                label: "ID",
                dataType: "text",
                sortable: false,
                filter: {
                    filterable: true,
                    type: "text",
                    strict: true
                },
                width: 120
            },
            {
                key: "name",
                label: "Nom",
                dataType: "text",
                sortable: false,
                filter: {
                    filterable: true,
                    type: "text",
                    fullText: true
                }
            },
            {
                key: "supplierItems",
                label: "Articles fournisseur",
                dataType: "text",
                sortable: false,
                filter: {
                    filterable: true,
                    type: "select",
                    withSearch: true,
                    options: supplierItems
                        .map((supplierItem) => ({
                            key: supplierItem.objectId,
                            value: supplierItem.name,
                            label: supplierItem.name
                        }))
                },
            },
            {
                key: "transformationModes",
                label: "Mode de transformation",
                dataType: "text",
                sortable: false,
                filter: {
                    filterable: true,
                    type: "select",
                    options: []
                },
            },
            {
                key: "updatedAt",
                label: "Date de modification",
                dataType: "date",
                type: "date",
                sortable: true,
                filter: {
                    filterable: false,
                    type: "date"
                },
                width: 130
            },
            {
                  key: "deletion",
                  label: "",
                  dataType: "text",
                  sortable: false,
                  filter: {
                    filterable: false,
                  },
                  width: 60,
                  render: (value) => {
                    return (
                      <Box sx={{ alignSelf: "stretch", display: "flex" }}>
                        <IconButton
                          sx={{ visibility: value === hoveredRowId ? "visible" : "hidden" }}
                          onClick={handleDelete(value)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )
                  }
                },
        ])
    }, [supplierItems, hoveredRowId])

    useEffect(() => {
        const formattedSteps = steps.map(step => ({
            id: step.objectId,
            name: step.name,
            supplierItems: filterSupplierItems(step.ingredients).map((ingredient) => ingredient.supplierItem.name).join(", "),
            transformationModes: step.transformations?.map((transformationMode) => transformationMode.transformationMode.name).join(", ") || "",
            updatedAt: dayjs(step.updatedAt).format("DD/MM/YYYY"),
            deletion: step.objectId
        }))

        setData(formattedSteps)
    }, [steps])

    const closeDeleteConfirmationDialog = () => setRowToDelete(null)

    const handleFilterChange = async (values) => {
        if (!values.supplierItems) return

        const selectedSupplierItem = await getSupplierItemByName(values.supplierItems, ["transformationModes.transformationMode"], false)

        if (!selectedSupplierItem) return

        const transformationModes = selectedSupplierItem.get("transformationModes") || []

        const transformationModesOptions = transformationModes.map((transformationMode) => ({
            key: transformationMode.transformationMode.id,
            value: transformationMode.transformationMode.get("name"),
            label: transformationMode.transformationMode.get("name")
        }))

        // get transformation modes options by selected supplier item filter
        setColumns(prev => prev.map(column => {
            if (column.key === "transformationModes") {

                column.filter.options = transformationModesOptions

                return column
            }
            return column
        }))
    }

    const handleRowClick = (step) => {
        dispatch(showReusableProductionStepView(step.id))
    }

    const handleResetFilters = (key) => {
        if (key !== "supplierItems") return

        setColumns(prev => prev.map(column => {
            if (column.key === "transformationModes") {
                column.filter.options = []

                return column
            }
            return column
        }))
    }

    const confirmDeletion = async (id) => {
        await dispatch(deleteReusableStep(id))
        closeDeleteConfirmationDialog()
    }

    return (
        <>
            <DataGrid
                title="Étapes réutilisables"
                columns={columns}
                data={data}
                withFilters
                rowLabel="étapes réutilisables"
                onRowClick={handleRowClick}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                localStorageKey={REUSABLE_STEP_DATAGRID_STORAGE}
                onRowMouseEnter={handleRowMouseEnter}
                onRowMouseLeave={handleRowMouseLeave}
            />

            {/* can not delete modal */}
            <CantDeleteModal
                open={openCanNotDeleteDialog}
                onClose={() => setOpenCanNotDeleteDialog(false)}
                title="Suppression de l'étape réutilisable impossible"
                description="Cette étape réutilisable est utilisée dans certaines recettes. Si vous souhaitez la supprimer, il faut d’abord la retirer des fiches techniques"
            />
            <DeleteConfirmationModal
                open={!!rowToDelete}
                onClose={closeDeleteConfirmationDialog}
                onConfirm={() => confirmDeletion(rowToDelete?.id)}
                description={`Vous avez choisi de supprimer l'étape réutilisable ${rowToDelete?.name}.\nCette action est irréversible.\nConfirmez-vous cette action ?`}
            />
            {/* KFC-565: recipes not updated after the reusable step edition */}
            <ReusableStepRebuildReportModal />
            {/* --------- add button --------- */}
            <Box sx={{ position: "fixed", left: 260, bottom: 20 }}>
                <Fab color="primary" onClick={goToReusableStepCreation}>
                    <Add />
                </Fab>
            </Box>
        </>
    )
}

export default ReusableSteps
